import JSZip from "jszip";
import { fetchAndParseCSV } from "../../../shared/csvParser";
import {
  generateReportPDF,
  generatePDFFilename,
  validateReportData,
} from "./pdfGenerator";

/**
 * Triggers browser file download from a Blob.
 * Creates a temporary anchor element, sets href to Object URL, and clicks it.
 *
 * @param {Blob} blob - PDF blob to download
 * @param {string} filename - Target filename
 */
export const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/**
 * Orchestrates the full PDF download workflow:
 * 1. Fetch and parse CSV/ZIP data
 * 2. Validate parsed data
 * 3. Generate PDF
 * 4. Trigger browser download
 * 5. Upload to backend (non-blocking / fire-and-forget)
 *
 * @param {Object} report - Report object with id, name, report_type, report_file_url, created_at
 * @param {Object} options
 * @param {Function} options.onLoadingStart - Called when workflow begins
 * @param {Function} options.onLoadingEnd - Called when workflow completes (success or failure)
 * @param {Function} options.onError - Called with error message on failure
 * @param {Function} options.onUploadError - Called with error message on upload failure
 * @param {Function} options.uploadMutation - RTK Query mutation function for uploading PDF
 * @returns {Promise<void>}
 */
export const executePDFDownload = async (report, options = {}) => {
  const {
    onLoadingStart,
    onLoadingEnd,
    onError,
    onUploadError,
  } = options;

  // Master AbortController with 60s timeout
  const masterController = new AbortController();
  const masterTimeout = setTimeout(() => masterController.abort(), 60000);

  // Notify loading start
  if (onLoadingStart) onLoadingStart();

  try {
    // Listen for master abort
    if (masterController.signal.aborted) {
      throw new Error("PDF operation timed out");
    }

    // Step 1: Fetch and parse CSV/ZIP data with 30s timeout
    let reportData;
    try {
      const fetchPromise = fetchAndParseCSV(report.report_file_url);
      const timeoutPromise = new Promise((_, reject) => {
        const id = setTimeout(
          () => reject(new Error("Fetch timeout")),
          30000
        );
        masterController.signal.addEventListener("abort", () => {
          clearTimeout(id);
          reject(new Error("PDF operation timed out"));
        });
      });
      reportData = await Promise.race([fetchPromise, timeoutPromise]);
    } catch (error) {
      console.error("[PDF Download] Fetch/parse failed:", error);
      if (masterController.signal.aborted) {
        throw new Error("PDF operation timed out");
      }
      throw new Error("Report data is unavailable");
    }

    // Check master abort between steps
    if (masterController.signal.aborted) {
      throw new Error("PDF operation timed out");
    }

    // Step 2: Validate parsed data
    const validation = validateReportData(reportData);
    if (!validation.valid) {
      console.error("[PDF Download] Validation failed:", validation.error);
      throw new Error(validation.error || "Report data is invalid");
    }

    // Step 3: Generate PDF
    let pdfBlob;
    try {
      pdfBlob = generateReportPDF(reportData);
    } catch (error) {
      console.error("[PDF Download] PDF generation error:", error);
      throw new Error("PDF generation failed");
    }

    // Check master abort before download
    if (masterController.signal.aborted) {
      throw new Error("PDF operation timed out");
    }

    // Step 4: Trigger browser download (fires independently of upload)
    const filename = generatePDFFilename(report);
    triggerBlobDownload(pdfBlob, filename);

    // Step 5: Upload to backend (fire-and-forget, does not block download)
    // Two-step flow:
    //   a. POST /reports/upload with { report_id } -> returns a presigned S3 URL
    //   b. Zip the generated PDF and PUT it to the presigned URL
    const uploadPromise = (async () => {
      try {
        const authToken = sessionStorage.getItem("fmdp-access-token");
        const uploadUrl = `${process.env.REACT_APP_TRACKING_API}/reports/upload`;

        // Step 5a: Request a presigned upload URL from the backend
        const presignFetch = fetch(uploadUrl, {
          method: "POST",
          headers: {
            Authorization: authToken || "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ report_id: String(report.id) }),
          signal: masterController.signal,
        });

        const presignTimeout = new Promise((_, reject) => {
          const id = setTimeout(() => reject(new Error("Upload timeout")), 30000);
          masterController.signal.addEventListener("abort", () => {
            clearTimeout(id);
            reject(new Error("PDF operation timed out"));
          });
        });

        const presignResponse = await Promise.race([presignFetch, presignTimeout]);
        if (!presignResponse.ok) {
          throw new Error(`Upload URL request failed with status ${presignResponse.status}`);
        }

        const presign = await presignResponse.json();
        const s3Url = presign.upload_url;
        const s3Method = (presign.method || "PUT").toUpperCase();
        const s3Headers = presign.headers || { "Content-Type": "application/zip" };

        if (!s3Url) {
          throw new Error("Upload URL missing from response");
        }

        // Step 5b: Zip the generated PDF for S3 upload
        const zip = new JSZip();
        zip.file(filename, pdfBlob);
        const zipBlob = await zip.generateAsync({
          type: "blob",
          compression: "DEFLATE",
        });

        // Enforce backend size limit when provided
        if (presign.max_size_bytes && zipBlob.size > presign.max_size_bytes) {
          throw new Error("Generated file exceeds maximum upload size");
        }

        // Step 5c: PUT the zip to the presigned S3 URL.
        // Only send the headers S3 expects (from the presign response); do NOT
        // send the Authorization header since auth is embedded in the URL.
        const s3Fetch = fetch(s3Url, {
          method: s3Method,
          headers: s3Headers,
          body: zipBlob,
          signal: masterController.signal,
        });

        const s3Timeout = new Promise((_, reject) => {
          const id = setTimeout(() => reject(new Error("Upload timeout")), 30000);
          masterController.signal.addEventListener("abort", () => {
            clearTimeout(id);
            reject(new Error("PDF operation timed out"));
          });
        });

        const s3Response = await Promise.race([s3Fetch, s3Timeout]);
        if (!s3Response.ok) {
          throw new Error(`S3 upload failed with status ${s3Response.status}`);
        }
      } catch (error) {
        if (onUploadError) {
          onUploadError("Upload failed");
        }
      }
    })();

    await uploadPromise;
  } catch (error) {
    if (onError) {
      onError(error.message || "PDF generation failed");
    }
  } finally {
    clearTimeout(masterTimeout);
    if (onLoadingEnd) onLoadingEnd();
  }
};
